import { useEffect, useRef, useState } from "react";
import { Camera, FileUp, FileText, CheckCircle2, X } from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf";

const Thumb = ({ preview, file, existing, label }) => {
  if (preview) return <img src={preview} alt={label} />;
  if (file) return <FileText size={22} />;
  if (existing) return <CheckCircle2 size={22} />;
  return <Camera size={22} />;
};

const statusText = (file, existing, hint) => {
  if (file) return file.name;
  if (existing) return `Tersimpan: ${existing}`;
  return hint;
};

const watermarkStamp = () => {
  const parts = new Intl.DateTimeFormat("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).formatToParts(new Date());
  const get = type => parts.find(part => part.type === type)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}:${get("second")} WIB`;
};

const stampPhoto = async file => {
  if (!file.type.startsWith("image/")) return file;
  const watermarkAt = watermarkStamp();
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width; canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    context.drawImage(bitmap, 0, 0); bitmap.close?.();
    const fontSize = Math.max(16, Math.round(canvas.width * 0.024));
    const padX = Math.round(fontSize * 0.65), padY = Math.round(fontSize * 0.4), margin = Math.round(fontSize * 0.75);
    context.font = `600 ${fontSize}px Arial, sans-serif`;
    const textWidth = context.measureText(watermarkAt).width;
    const boxWidth = textWidth + padX * 2, boxHeight = fontSize + padY * 2;
    const x = canvas.width - boxWidth - margin, y = canvas.height - boxHeight - margin;
    context.fillStyle = "rgba(0,0,0,.68)";
    context.beginPath(); context.roundRect(x, y, boxWidth, boxHeight, Math.max(4, fontSize * .25)); context.fill();
    context.fillStyle = "#fff"; context.textBaseline = "middle"; context.fillText(watermarkAt, x + padX, y + boxHeight / 2);
    const type = ["image/jpeg", "image/png", "image/webp"].includes(file.type) ? file.type : "image/jpeg";
    const blob = await new Promise(resolve => canvas.toBlob(resolve, type, .94));
    if (!blob) throw new Error("Photo cannot be watermarked");
    const result = new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type, lastModified: file.lastModified });
    Object.defineProperty(result, "watermarkAt", { value: watermarkAt });
    return result;
  } catch {
    // Leave it unstamped so the server can add the watermark as a fallback.
    return file;
  }
};

export const AttachmentField = ({ kind, label, hint, files = [], existing = [], onChange, onRemove, onProcessing, processing = false }) => {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const pendingPicks = useRef(0);
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map(file => file.type.startsWith("image/") ? URL.createObjectURL(file) : "");
    setPreviews(urls);
    return () => urls.forEach(url => url && URL.revokeObjectURL(url));
  }, [files]);
  const pick = async (e) => {
    const picked = Array.from(e.target.files || []); e.target.value = "";
    if (!picked.length) return;
    pendingPicks.current += 1;
    onProcessing?.(true);
    try { onChange(await Promise.all(picked.map(stampPhoto))); }
    finally { pendingPicks.current = Math.max(0, pendingPicks.current - 1); onProcessing?.(pendingPicks.current > 0); }
  };
  const remove = index => onRemove(index);
  const hasFiles = files.length || existing.length;
  return <div className={`attach-field ${hasFiles ? "filled" : ""}`} data-testid={`attach-${kind}`}>
    <div className="attach-thumb"><Thumb preview={previews[0]} file={files[0]} existing={existing[0]?.filename} label={label} /></div>
    <div className="attach-body">
      <strong>{label} <span className="req">*</span></strong>
      <span className="muted">{processing ? "Menambahkan watermark tanggal dan waktu…" : hasFiles ? `${files.length ? `${files.length} foto dipilih` : ""}${existing.length ? `${files.length ? " · " : ""}${existing.length} tersimpan` : ""}` : hint}</span>
      <div className="attach-actions">
        <button type="button" data-testid={`attach-${kind}-camera`} className="attach-btn primary" disabled={processing} onClick={() => cameraRef.current?.click()}><Camera size={15} /> {hasFiles ? "Tambah foto" : "Buka kamera"}</button>
        <button type="button" data-testid={`attach-${kind}-file`} className="attach-btn" disabled={processing} onClick={() => fileRef.current?.click()}><FileUp size={15} /> Pilih dari perangkat</button>
      </div>
    </div>
    <input ref={cameraRef} data-testid={`attach-${kind}-camera-input`} type="file" accept="image/*" capture="environment" disabled={processing} hidden onChange={pick} />
    <input ref={fileRef} data-testid={`attach-${kind}-file-input`} type="file" accept={ACCEPT} multiple disabled={processing} hidden onChange={pick} />
    {(files.length > 0 || existing.length > 0) && <div className="attachment-file-list">{existing.map((item,index)=><div key={`old-${index}`}>{item.filename}<span>Tersimpan</span></div>)}{files.map((item,index)=><div key={`new-${index}`}>{previews[index]&&<img src={previews[index]} alt=""/>}<span>{item.name}</span>{item.watermarkAt&&<small>{item.watermarkAt}</small>}<button type="button" onClick={()=>remove(index)} aria-label={`Hapus ${item.name}`}><X size={14}/></button></div>)}</div>}
  </div>;
};
